import {
    IconFeather,
    IconSend,
    IconTrash,
    IconUser
} from '@tabler/icons-react';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    ActionIcon,
    Badge,
    Divider,
    Drawer,
    Group,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Switch,
    Text,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import type { ChatMessage, ChatResponse } from '../../../api/models';
import {
    usePostApiOpenAIChat,
    usePostApiOpenAIVectorChat
} from '../../../api/open-a-i/open-a-i';

interface DocumentChatDrawerProps {
    opened: boolean;
    onClose: () => void;
    documentContent: string;
}

interface StoredMessage {
    role: string;
    content: string;
    timestamp: string;
}

interface MessageWithTimestamp extends ChatMessage {
    timestamp: Date;
}

const STORAGE_PREFIX = 'firedocs-chat-';

function getStorageKey(documentId: string) {
    return `${STORAGE_PREFIX}${documentId}`;
}

function loadMessages(documentId: string): MessageWithTimestamp[] {
    try {
        const raw = sessionStorage.getItem(getStorageKey(documentId));
        if (!raw) return [];
        const stored = JSON.parse(raw) as StoredMessage[];
        return stored.map(m => ({
            ...m,
            timestamp: new Date(m.timestamp)
        }));
    } catch {
        return [];
    }
}

function saveMessages(documentId: string, messages: MessageWithTimestamp[]) {
    const toStore: StoredMessage[] = messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString()
    }));
    sessionStorage.setItem(getStorageKey(documentId), JSON.stringify(toStore));
}

export function DocumentChatDrawer({
    opened,
    onClose,
    documentContent
}: DocumentChatDrawerProps) {
    const { documentId } = useParams<{ documentId: string }>();
    const [messages, setMessages] = useState<MessageWithTimestamp[]>(() =>
        documentId ? loadMessages(documentId) : []
    );
    const [inputMessage, setInputMessage] = useState('');
    const [useVectorSearch, setUseVectorSearch] = useState(false);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const chatMutation = usePostApiOpenAIChat();
    const vectorChatMutation = usePostApiOpenAIVectorChat();

    // Restore messages when document changes
    useEffect(() => {
        if (documentId) {
            setMessages(loadMessages(documentId));
        }
    }, [documentId]);

    // Persist messages on change
    useEffect(() => {
        if (documentId) {
            saveMessages(documentId, messages);
        }
    }, [documentId, messages]);

    useEffect(() => {
        // Scroll to bottom when new messages are added
        if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop =
                scrollAreaRef.current.scrollHeight;
        }
    }, [messages]);

    const clearChat = useCallback(() => {
        setMessages([]);
        if (documentId) {
            sessionStorage.removeItem(getStorageKey(documentId));
        }
    }, [documentId]);

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        const userMessage: MessageWithTimestamp = {
            role: 'user',
            content: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');

        try {
            let response: ChatResponse | null | undefined;

            if (useVectorSearch) {
                // Use vector store chat (Responses API) with document comparison
                response = await vectorChatMutation.mutateAsync({
                    data: {
                        message: inputMessage,
                        documentContent
                    }
                });
            } else {
                // Use regular chat with document context
                const conversationHistory: ChatMessage[] = messages.map(
                    ({ role, content }) => ({
                        role,
                        content
                    })
                );

                response = await chatMutation.mutateAsync({
                    data: {
                        message: inputMessage,
                        documentContent,
                        conversationHistory:
                            conversationHistory.length > 0
                                ? conversationHistory
                                : null
                    }
                });
            }

            if (response.success && response.reply) {
                const assistantMessage: MessageWithTimestamp = {
                    role: 'assistant',
                    content: response.reply,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, assistantMessage]);
            } else if (response.error) {
                notifications.show({
                    title: 'Chat Error',
                    message: response.error,
                    color: 'red'
                });
            }
        } catch {
            notifications.show({
                title: 'Connection Error',
                message:
                    'Failed to connect to the chat service. Please try again.',
                color: 'red'
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSendMessage();
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position='right'
            size='lg'
            title={
                <Group>
                    <IconFeather size={24} />
                    <Title order={4}>Firefinch Document Assistant</Title>
                </Group>
            }
            styles={{
                body: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 80px)',
                    maxHeight: 'calc(100vh - 80px)',
                    padding: 0
                }
            }}
        >
            <Stack h='100%' gap={0}>
                <Paper p='md' withBorder>
                    <Stack gap='md'>
                        <Text size='sm' c='dimmed'>
                            I can help you improve the content provided from
                            markdown. Ask me about structure, content, grammar,
                            or any questions you have about the content.
                        </Text>

                        <Group justify='space-between'>
                            <Group gap='xs'>
                                <Tooltip
                                    multiline
                                    w={300}
                                    label='Compare With Existing Proposals: When enabled, the assistant can access and reference information from existing proposals and previous conversations in your organisation to provide more comprehensive answers. When disabled, responses are based only on the current markdown content.'
                                >
                                    <Switch
                                        checked={useVectorSearch}
                                        onChange={event =>
                                            setUseVectorSearch(
                                                event.currentTarget.checked
                                            )
                                        }
                                        label='Compare With Existing Proposals'
                                        size='sm'
                                    />
                                </Tooltip>
                            </Group>
                            {messages.length > 0 && (
                                <Tooltip label='Clear chat history'>
                                    <ActionIcon
                                        variant='subtle'
                                        color='gray'
                                        size='sm'
                                        onClick={clearChat}
                                    >
                                        <IconTrash size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    </Stack>
                </Paper>

                <ScrollArea
                    flex={1}
                    p='md'
                    viewportRef={scrollAreaRef}
                    styles={{
                        viewport: {
                            '& > div': {
                                display: 'flex!important',
                                flexDirection: 'column',
                                minHeight: '100%'
                            }
                        }
                    }}
                >
                    <Stack gap='md' style={{ minHeight: '100%' }}>
                        {messages.length === 0 ? (
                            <Text c='dimmed' ta='center' mt='xl'>
                                Start a conversation about the markdown
                                content...
                            </Text>
                        ) : (
                            messages.map((message, index) => (
                                <Paper
                                    key={index}
                                    p='md'
                                    radius='md'
                                    withBorder
                                    style={{
                                        alignSelf:
                                            message.role === 'user'
                                                ? 'flex-end'
                                                : 'flex-start',
                                        maxWidth: '85%',
                                        backgroundColor:
                                            message.role === 'user'
                                                ? 'var(--mantine-color-blue-light)'
                                                : 'var(--mantine-color-gray-light)'
                                    }}
                                >
                                    <Group gap='xs' mb='xs'>
                                        {message.role === 'user' ? (
                                            <IconUser size={16} />
                                        ) : (
                                            <IconFeather size={16} />
                                        )}
                                        <Badge size='sm' variant='light'>
                                            {message.role === 'user'
                                                ? 'You'
                                                : 'Assistant'}
                                        </Badge>
                                        <Text size='xs' c='dimmed'>
                                            {message.timestamp.toLocaleTimeString()}
                                        </Text>
                                    </Group>
                                    <Text
                                        size='sm'
                                        style={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {message.content}
                                    </Text>
                                </Paper>
                            ))
                        )}
                        {(chatMutation.isPending ||
                            vectorChatMutation.isPending) && (
                            <Group gap='xs' align='center'>
                                <Loader size='sm' />
                                <Text size='sm' c='dimmed'>
                                    {useVectorSearch
                                        ? 'Searching existing proposals...'
                                        : 'Assistant is thinking...'}
                                </Text>
                            </Group>
                        )}
                    </Stack>
                </ScrollArea>

                <Divider />

                <Paper p='md' withBorder>
                    <Group gap='xs'>
                        <TextInput
                            flex={1}
                            placeholder='Ask about the markdown content...'
                            value={inputMessage}
                            onChange={e => setInputMessage(e.target.value)}
                            onKeyDown={handleKeyPress}
                            disabled={
                                chatMutation.isPending ||
                                vectorChatMutation.isPending
                            }
                            styles={{
                                input: {
                                    border: 'none'
                                }
                            }}
                        />
                        <ActionIcon
                            size='lg'
                            variant='filled'
                            onClick={() => void handleSendMessage()}
                            disabled={
                                !inputMessage.trim() ||
                                chatMutation.isPending ||
                                vectorChatMutation.isPending
                            }
                        >
                            <IconSend size={18} />
                        </ActionIcon>
                    </Group>
                </Paper>
            </Stack>
        </Drawer>
    );
}
